import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaExclamationTriangle, FaFileExcel, FaPlus, FaPrint, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import UseFetchOptions from "../../hooks/use-fetch-options.js";
import {Modal} from "../../../../../components/modal/modal.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import ChangeQuantity from "../components/change-quantity.jsx";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import {FaFileExport} from "react-icons/fa6";

const initialFilter = {
    storage: "",
    group: "",
    material: "",
}

const MaterialXStorageList = () => {
    const navigate = navigateHook();
    const [sum, setSum] = useState({total: 0, caution: 0, available: 0, unavailable: 0})
    const [isLoading, setIsLoading] = useState(false)
    const [filter, setFilter] = useState(initialFilter);
    const [openModalBreakdown, setOpenModalBreakdown] = useState(false)
    const [gridApi, setGridApi] = useState(null);
    const [breakdown, setBreakdown] = useState("")
    const [id, setId] = useState("")
    const gridRef = useRef(null);
    const [groupByMaterial, setGroupByMaterial] = useState(false)
    const {storageOptions, groupsOptions} = UseFetchOptions({fetchStorage: true, fetchGroups: true});

    const initialColumns = [
        {field: "id", hide: true},
        {field: "is_unique", hide: true, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams},
        {field: "storage", headerName: "Depósito", filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams,comparator: aggridUtils.lowerUpCaseComparator},
        {field: "group", headerName: "Grupo", filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "material", headerName: "Material", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator,
            cellRenderer: ({data, value}) => {
                if (data.breakdown) {
                    const openModal = () => {
                        setBreakdown(data.breakdown)
                        setId(data.id)
                        setOpenModalBreakdown(true)
                    }

                    return <div className={"flex items-center gap-2"}>
                        <FaExclamationTriangle onClick={openModal} color={"orange"} title={"Editar avaria"} className={"cursor-pointer"}/>
                        {value}
                    </div>
                }

                return value
            }
        },
        {field: "quantity", headerName: "Total", width: 80, comparator: aggridUtils.nullLastComparator},
        {field: "caution", headerName: "Em cautela",  width: 115, comparator: aggridUtils.nullLastComparator,
            cellRenderer: ({data}) => {
                if (data.caution > 0) {
                    const openMaterial = () => {
                        const urlDev = `/materials/cautions?material=${data.material}`;
                        navigate(urlDev, true)
                    }

                    return <span className={"underline cursor-pointer"} onClick={openMaterial}>
                        {data.caution}
                    </span>
                }

                return <></>
            }
        },
        {field: "available", headerName: "Disponível", width: 115},
        {field: "unavailable", headerName: "Indisponível", width: 115},
    ]

    const columnsGroup = [
        {field: "material", headerName: "Material", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams},
        {field: "quantity_total", headerName: "Total", width: 80},
    ]

    const [columnDefs, setColumns] = useState(initialColumns)

    useEffect(() => {
        clearFilter()

        if (groupByMaterial) {
            setColumns(columnsGroup)
        } else {
            setColumns(initialColumns)
        }
    }, [groupByMaterial]);

    const defaultColDef = useMemo(() => {
        return {
            suppressRowHoverHighlight: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: materials = [], isLoading: isLoadingList, refetch } = useQuery({
        queryKey: ['materials-x-storage'],
        queryFn: async () => {
            const { data } = await materialXStorageService.list();
            return data;
        },
    });

    useEffect(() => {
        const result = materials.reduce(
            (acc, item) => {
                acc.total += item.quantity;
                acc.caution += item.caution;
                acc.available += item.available;
                acc.unavailable += item.unavailable
                return acc;
            },
            { total: 0, caution: 0, available: 0, unavailable: 0 }
        );

        setSum(result);
    }, [materials]);

    useEffect(() => {
        setIsLoading(isLoadingList)
    }, [isLoadingList]);

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value?.label || value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const openPrint = () => {
        const filters = {
            storage: filter.storage?.value ?? "",
            storageLabel: filter.storage?.label ?? "",

            group: filter.group?.value ?? "",
            groupLabel: filter.group?.label ?? "",

            material: filter.material ?? ""
        };

        const encoded = btoa(encodeURIComponent(JSON.stringify(filters)));
        navigate(`/reports/material-x-storage-info?f=${encoded}`, true);
    };

    const exportData = () => {
        const filteredData = [];
        gridApi.forEachNodeAfterFilter(node => {
            filteredData.push(node.data);
        });

        const csvDatas = [];

        const csvMaker = (filteredData) => Object.values(filteredData).join(';');

        const newArray = filteredData.map(material => {
            return {
                "Depósito": material.storage,
                "Grupo": material.group,
                "Material": material.material,
                "Total": material.quantity,
                "Em cautela": material.caution,
                "Disponível": material.available,
                "Indisponível": material.unavailable
            };
        });

        const headers = Object.keys(newArray[0]);

        csvDatas.push(headers.join(';'));

        newArray.map((item) => csvDatas.push(csvMaker(item)));

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvDatas.join('\n')], { type: 'text/csv;charset=utf-8' });

        const url = window.URL.createObjectURL(blob);

        window.open(url);
        window.URL.revokeObjectURL(url);
    }

    const updateBreakdown = () => {
        setIsLoading(true)

        const formData = new FormData()
        formData.append("breakdown", breakdown)
        formData.append("id", id)

        materialXStorageService.updateBreakdown(formData)
            .then(() => {
                setOpenModalBreakdown(false)
                refetch()
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const getRowData = () => {
        const materialsGrouped = Object.values(
            materials.reduce((acc, item) => {
                if (!acc[item.material_id]) {
                    acc[item.material_id] = {
                        material_id: item.material_id,
                        material: item.material_name,
                        quantity_total: 0
                    };
                }

                acc[item.material_id].quantity_total += item.quantity;

                return acc;
            }, {}))

        return groupByMaterial ? materialsGrouped : materials;
    }

    const hasPermission = useHasPermission();
    const hasAdjustmentPermission = hasPermission(PermissionEnum.ERP_MATERIALS_ADJUSTMENT);

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Materiais por depósito
                    </Template.Title>

                    <div className={"flex gap-2"}>
                        {
                            hasAdjustmentPermission &&
                            <ChangeQuantity defaultColDef={defaultColDef} materials={materials} setIsLoading={setIsLoading} refetch={refetch}/>
                        }

                        <Button target={"success"} onClick={exportData}>
                            <FaFileExport/> <span className="font-bold">Exportar</span>
                        </Button>

                        <Button title="Imprimir" onClick={openPrint}>
                            <FaPrint/>
                        </Button>
                    </div>
                </Template.Header>
                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className="col-span-3">
                                    <SelectComponent
                                        isDisabled={groupByMaterial}
                                        id="storage"
                                        value={filter.storage?.value || ""}
                                        options={storageOptions || []}
                                        label={"Depósito"}
                                        onChange={(selected) => setFilter(prevState => ({...prevState, storage: selected}))}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <SelectComponent
                                        isDisabled={groupByMaterial}
                                        id="group"
                                        value={filter.group?.value || ""}
                                        options={groupsOptions || []}
                                        label={"Grupo"}
                                        onChange={(selected) => setFilter(prevState => ({...prevState, group: selected}))}
                                    />
                                </div>
                                <div className="col-span-6 flex gap-4">
                                    <InputForm id="material" name="material" value={filter.material} onChange={onChange} label="Material" classNameWrapper={"w-full"}/>
                                    <div className="flex justify-start gap-2">
                                        <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                            <FaEraser/>
                                        </Button>
                                        <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>
                    <Card className="mt-2">
                        <Card.Body>
                            <div className="mb-3">
                                <InputCheckbox
                                    id="group-material"
                                    checked={groupByMaterial}
                                    label="Agrupar por material"
                                    onChange={({target: {checked}}) => setGroupByMaterial(checked)}/>
                            </div>

                            <div className={"h-[calc(100vh-185px)]"}>
                                <AgGridReact
                                    onGridReady={(params) => {
                                        setGridApi(params.api);
                                    }}
                                    ref={gridRef}
                                    rowData={getRowData()}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    pagination
                                    paginationPageSize={50}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme({noHover: true})}
                                    enableCellTextSelection={true}
                                    suppressMovableColumns={true}
                                    onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                                />

                                <div className={"absolute flex gap-5 bottom-[15px] left-[25px]"}>
                                    <div className={"gap-1 flex items-center"}>
                                        <span className={"bg-[#ddd] p-2 border border-[#333] rounded-xs h-5 flex justify-center items-center font-bold text-[14px]"}>{sum.total || 0}</span>
                                        <span className={"text-[14px]"}>Total</span>
                                    </div>
                                    <div className={"gap-1 flex items-center"}>
                                        <span className={"bg-[#ddd] p-2 border border-[#333] rounded-xs h-5 flex justify-center items-center font-bold text-[14px]"}>{sum.caution || 0}</span>
                                        <span className={"text-[14px]"}>Em cautela</span>
                                    </div>
                                    <div className={"gap-1 flex items-center"}>
                                        <span className={"bg-[#ddd] p-2 border border-[#333] rounded-xs h-5 flex justify-center items-center font-bold text-[14px]"}>{sum.available || 0}</span>
                                        <span className={"text-[14px]"}>Disponível</span>
                                    </div>
                                    <div className="gap-1 flex items-center">
                                        <span className="bg-[#ddd] p-2 border border-[#333] rounded-xs h-5 flex justify-center items-center font-bold text-[14px]">{sum.unavailable || 0}</span>
                                        <span className="text-[14px]">Indisponível</span>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <Modal alignTop={true} open={openModalBreakdown} setOpen={setOpenModalBreakdown} title={"Editar avaria"} className={"w-3/12"}>
                <Modal.Body className={"p-2 grid grid-cols-12 gap-3"}>
                    <InputTextarea
                        id={"breakdown"}
                        className={"col-span-12"}
                        textAreaClass={"h-40"}
                        label={"Descrição"}
                        value={breakdown}
                        onChange={(e) => setBreakdown(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button target="primary" onClick={updateBreakdown}>
                        Atualizar
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    )
};

export default MaterialXStorageList;